import React, { useEffect, useRef, useState } from "react";

interface MasonryColumnsProps<T> {
  items: T[];
  cardWidth: number;
  cardGap: number;
  maxColumns?: number;
  centerAlignColumns?: boolean;
  renderItem: (item: T, index: number) => React.ReactNode;
}

export function MasonryColumns<T>({
  items,
  cardWidth,
  cardGap,
  maxColumns = 3,
  centerAlignColumns = false,
  renderItem,
}: MasonryColumnsProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const firstCardRef = useRef<HTMLDivElement>(null);
  const [gridCols, setGridCols] = useState(1);

  useEffect(() => {
    const updateGridCols = () => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.offsetWidth;
      const possibleCols = Math.floor(containerWidth / (cardWidth + cardGap));
      const clampedCols = Math.min(Math.max(possibleCols, 1), maxColumns);
      setGridCols(clampedCols);
    };

    const resizeObserver = new ResizeObserver(updateGridCols);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener("resize", updateGridCols);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateGridCols);
    };
  }, [cardWidth, cardGap, maxColumns]);

  const effectiveCols = centerAlignColumns ? Math.min(gridCols, items.length) : gridCols;

  const organizeIntoColumns = () => {
    const cols = Array.from({ length: effectiveCols }, () => [] as typeof items);
    items.forEach((item, i) => {
      cols[i % effectiveCols].push(item);
    });
    return cols;
  };

  const columnData = React.useMemo(organizeIntoColumns, [items, effectiveCols]);

  return (
    <div
      className={`w-full ${centerAlignColumns ? "flex justify-center" : "flex justify-start"}`}
      ref={containerRef}
    >
      <div
        className="grid"
        style={{
          gridTemplateColumns: `repeat(${columnData.length}, ${cardWidth}px)`,
          columnGap: `${cardGap}px`,
        }}
      >
        {columnData.map((column, colIndex) => (
          <div
            key={colIndex}
            className="masonry-column flex flex-col"
            style={{ rowGap: `${cardGap}px` }}
          >
            {column.map((item, rowIndex) => (
              <div
                key={`${colIndex}-${rowIndex}`}
                ref={colIndex === 0 && rowIndex === 0 ? firstCardRef : undefined}
              >
                {renderItem(item, rowIndex)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
